import React, { useState, useEffect } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { appConfigApi } from '../../services/appConfigApi';
import { AppConfig, EntityConfig } from '../../types';
import Layout from '../../components/Layout';
import { Save, AlertCircle, Check } from 'lucide-react';

const AppConfigPage: React.FC = () => {
    const { t } = useI18n();
    const [config, setConfig] = useState<AppConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        try {
            setLoading(true);
            const data = await appConfigApi.getAppConfig();
            // Ensure defaultEntityConfig is initialized if null
            if (!data.defaultEntityConfig) {
                data.defaultEntityConfig = {
                    id: '',
                    name: 'Default Plan',
                    entityId: 'default',
                    maxCompanies: 1,
                    stockFeatureEnabled: false,
                    customTemplatesEnabled: false,
                    encryptionEnabled: false,
                    packageId: '',
                    settings: {},
                    creationDate: new Date().toISOString(),
                    modificationDate: new Date().toISOString(),
                };
            }
            setConfig(data);
        } catch (err) {
            setError('Failed to fetch application configuration');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!config) return;

        try {
            setSaving(true);
            setError(null);
            setSuccess(null);
            await appConfigApi.updateAppConfig(config);
            setSuccess('Configuration saved successfully');
        } catch (err) {
            setError('Failed to save configuration');
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const updateDefaultEntityConfig = (field: keyof EntityConfig, value: any) => {
        if (!config || !config.defaultEntityConfig) return;
        setConfig({
            ...config,
            defaultEntityConfig: {
                ...config.defaultEntityConfig,
                [field]: value,
            },
        });
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-screen">
                    <div className="w-12 h-12 spinner"></div>
                </div>
            </Layout>
        );
    }

    if (!config) return null;

    return (
        <Layout>
            <div className="p-6 max-w-4xl mx-auto">
                <div className="mb-8">
                    <h1 className="text-2xl font-bold text-gray-800">Application Configuration</h1>
                    <p className="text-gray-600">Manage global settings and default user configurations.</p>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
                        <AlertCircle size={20} />
                        {error}
                    </div>
                )}

                {success && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 text-green-700">
                        <Check size={20} />
                        {success}
                    </div>
                )}

                <form onSubmit={handleSave} className="space-y-8">
                    {/* General Settings Section */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">General Settings</h2>

                        <div className="flex items-center gap-3">
                            <div className="relative inline-block w-12 h-6 transition duration-200 ease-in-out">
                                <input
                                    type="checkbox"
                                    id="registrationEnabled"
                                    className="peer absolute w-12 h-6 opacity-0 z-10 cursor-pointer"
                                    checked={config.registrationEnabled}
                                    onChange={(e) => setConfig({ ...config, registrationEnabled: e.target.checked })}
                                />
                                <label
                                    htmlFor="registrationEnabled"
                                    className={`block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer transition-colors duration-200 peer-checked:bg-blue-600`}
                                ></label>
                                <span className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 peer-checked:translate-x-6`}></span>
                            </div>
                            <label htmlFor="registrationEnabled" className="text-gray-700 font-medium cursor-pointer">
                                Enable User Registration
                            </label>
                        </div>
                        <p className="mt-2 text-sm text-gray-500 mb-6">
                            If disabled, new users will not be able to sign up. Existing users can still log in.
                        </p>

                        <div className="border-t pt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Free Days for New Subscribers</label>
                            <input
                                type="number"
                                min="0"
                                value={config.freeDays || 0}
                                onChange={(e) => setConfig({ ...config, freeDays: parseInt(e.target.value) })}
                                className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="e.g. 30"
                            />
                            <p className="mt-1 text-xs text-gray-400">
                                Number of days of free access granted to new subscribers upon registration.
                            </p>
                        </div>
                    </div>

                    {/* Default Entity Configuration Section */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Default User Configuration</h2>
                        <p className="text-sm text-gray-500 mb-6">
                            These settings will be applied to new users when they register and verify their email.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Configuration Name</label>
                                <input
                                    type="text"
                                    value={config.defaultEntityConfig?.name || ''}
                                    onChange={(e) => updateDefaultEntityConfig('name', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="e.g. Free Plan"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Max Companies</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={config.defaultEntityConfig?.maxCompanies || 1}
                                    onChange={(e) => updateDefaultEntityConfig('maxCompanies', parseInt(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Package ID (Optional)</label>
                                <input
                                    type="text"
                                    value={config.defaultEntityConfig?.packageId || ''}
                                    onChange={(e) => updateDefaultEntityConfig('packageId', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="e.g. pkg_free_01"
                                />
                            </div>
                        </div>

                        <div className="mt-6 space-y-4">
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="stockFeatureEnabled"
                                    checked={config.defaultEntityConfig?.stockFeatureEnabled || false}
                                    onChange={(e) => updateDefaultEntityConfig('stockFeatureEnabled', e.target.checked)}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="stockFeatureEnabled" className="text-sm font-medium text-gray-700">
                                    Enable Stock Features (Orders, Movements)
                                </label>
                            </div>

                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="customTemplatesEnabled"
                                    checked={config.defaultEntityConfig?.customTemplatesEnabled || false}
                                    onChange={(e) => updateDefaultEntityConfig('customTemplatesEnabled', e.target.checked)}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="customTemplatesEnabled" className="text-sm font-medium text-gray-700">
                                    Enable Custom Templates
                                </label>
                            </div>

                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="encryptionEnabled"
                                    checked={config.defaultEntityConfig?.encryptionEnabled || false}
                                    onChange={(e) => updateDefaultEntityConfig('encryptionEnabled', e.target.checked)}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="encryptionEnabled" className="text-sm font-medium text-gray-700">
                                    Enable Encryption Features
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                        >
                            {saving ? (
                                <>
                                    <div className="w-5 h-5 spinner border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save size={20} />
                                    Save Configuration
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </Layout>
    );
};

export default AppConfigPage;
